<template>
  <div class="how-to-buy">
    <div class="how-to-buy-card" @click.prevent="toggle">
      <slot />
    </div>
    <div
      class="how-to-buy-container-wrapper"
      :class="[
        {
          share,
          'how-to-buy-container-wrapper__show': share,
        },
      ]"
    >
      <div class="how-to-buy-container">
        <UiUiButtonIco
          class="icon-close"
          size-ico="24"
          type="tertiary"
          icon="close"
          @click="toggle"
        />
        <div ref="popap" class="how-to-buy-title">Как купить товар?</div>
        <div class="how-to-buy-content">
          <template v-if="type === 'listing' || type === 'search'">
            <p>
              <span class="bold">Мы не продаем товары</span>, а помогаем вам
              сравнить цены в интернет-магазинах и найти где дешевле купить
              {{ title }}
            </p>
            <p>
              Чтобы купить {{ title2 }}, изучите предложения в каталоге или
              нажмите на кнопку «Сравнить цены» для перехода к детальной
              информации о товаре (цены, отзывы, фото, характеристики, оплаты и
              доставки).
            </p>
            <p>
              Когда вы выбрали нужный товар и предложение на сайте, вы можете
              перейти к покупке следующими способами:
            </p>
          </template>
          <template v-else-if="type === 'model'">
            <p>
              Чтобы купить {{ title }} изучите {{ offerCount }} от
              {{ shopCount }}. Изучите цены, рейтинги, отзывы, характеристики,
              фото, условия оплаты и доставки.
            </p>
            <p>
              <span class="bold">Мы не продаем товары</span>, а помогаем вам
              сравнить цены в интернет-магазинах и найти где дешевле купить
              {{ title2 }}. Когда вы выбрали нужный товар и предложение на
              сайте, вы можете перейти к покупке следующими способами:
            </p>
          </template>
          <ul>
            <li>
              — <span class="bold">Нажать на кнопку «В магазин»</span>, перейти
              на сайт продавца и оформить заказ через корзину его
              интернет-магазина
            </li>
            <li>
              — <span class="bold">Нажать на кнопку «Сделать заказ»</span>,
              оставить контактные данные, и продавец свяжется с вами для
              уточнения деталей
            </li>
          </ul>
          <UiLink path="/kak-oformit-zakaz/" mode="link" icon-after="right">
            Читать полную инструкцию
          </UiLink>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useScrollLock, onClickOutside, useMagicKeys } from '@vueuse/core';
import UiLink from '~/components/ui/ui-link/ui-link.vue';
import { useGlobalStore } from '~/stores/global';
import numWord from '~/common/num-word';
import { base64ToObj } from '~/utils/base64';

const props = defineProps({
  type: {
    type: String,
    default: 'listing',
  },
  count: {
    type: String,
    default: '',
  },
});
const route = useRoute();
const globalStore = useGlobalStore();

const html = ref<HTMLElement | null>(null);
const popap = ref<HTMLElement | null>(null);
const isLocked = useScrollLock(html);
const share = ref<boolean>(false);
const title = computed(() => {
  const h1 =
    (props.type === 'search'
      ? decodeURI(String(route?.query?.query))
      : globalStore.seoData?.page?.h1) || '';
  if (globalStore.region.id === 1) return `${h1} в Москве`;
  return h1;
});
const title2 = computed(() => {
  return (
    (props.type === 'search'
      ? decodeURI(String(route?.query?.query))
      : globalStore.seoData?.page?.h1) || ''
  );
});
const offerCount = computed(() => {
  if (props.type === 'model') {
    return props.count;
  }
  return '';
});
const wordEnd = (val: number) => {
  return numWord(
    val,
    ['', 'интернет-магазина', 'интернет-магазинов', 'интернет-магазинов'],
    true
  );
};
const shopCount = computed(() => {
  if (props.type === 'model') {
    const model = base64ToObj(globalStore.pageDistributorData).data?.model;
    return model?.shop_count ? wordEnd(model.shop_count) : '';
  }
  return base64ToObj(globalStore.pageDistributorData).data?.model;
});
const toggle = () => {
  share.value = !share.value;
  isLocked.value = share.value;
};
const { escape } = useMagicKeys();
watch(escape, (v) => {
  if (v) {
    share.value = false;
    isLocked.value = true;
  }
});
onMounted(() => {
  html.value = document.querySelector('html');
  onClickOutside(popap, (e) => {
    setTimeout(() => {
      const target = e.target as HTMLElement;
      if (
        target?.className &&
        typeof target?.className?.includes === 'function' &&
        target.className.includes('how-to-buy-container-wrapper')
      ) {
        share.value = false;
        isLocked.value = false;
      }
    }, 100);
  });
});
</script>

<style scoped lang="scss">
.how-to-buy {
  position: relative;
  display: flex;
  align-items: center;
  margin-left: 0;

  .how-to-buy-container-wrapper {
    transition: height 0s 0.2s, background-color 0.2s, opacity 0s;
    border-radius: 0;
    border: none;
    box-shadow: none;
    display: none;

    &.share {
      position: fixed;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      background-color: rgba(0, 0, 0, 0.6);
      width: 100vw;
      height: auto;
      z-index: 153;
      padding-top: 98px;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      align-items: stretch;
      opacity: 1;
      @include lg-block {
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    &:after {
      display: none;
    }

    .how-to-buy-container {
      display: flex;
      position: relative;
      width: 100%;
      background: $white;
      flex-direction: column;
      align-items: flex-start;
      border-radius: 8px 8px 0 0;
      padding: 16px 12px 32px;
      white-space: initial;
      @include lg-block {
        width: 598px;
        border-radius: 8px;
        height: unset;
        padding: 32px;
        border: none;
        box-shadow: 0 4px 20px rgba(36, 36, 36, 0.2);
      }
    }

    &__show {
      display: initial;
    }

    &:after {
      position: absolute;
      content: '';
      width: 30px;
      height: 100%;
      right: -30px;
      top: 0;
    }

    .icon-close {
      position: absolute;
      right: 0;
      top: 0;
      cursor: pointer;
    }
  }

  .how-to-buy-card {
    display: flex;
    align-items: center;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    position: relative;
    @include md-block {
      flex-direction: column;
    }
    @include lg-block {
      font-size: 14px;
      line-height: 20px;
    }
  }

  .how-to-buy-title {
    margin-bottom: 8px;
    color: $achromatic-black;
    font-size: 16px;
    font-style: normal;
    font-weight: 800;
    line-height: 20px;
    text-transform: uppercase;
    @include lg-block {
      margin-bottom: 16px;
      font-size: 24px;
      line-height: 32px;
    }
  }

  .how-to-buy-content {
    color: $achromatic-black;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;

    :deep(p) {
      margin-bottom: 8px;
    }

    :deep(li) {
      margin-bottom: 8px;
    }

    :deep(ul) {
      margin-bottom: 12px;
      @include lg-block {
        margin-bottom: 28px;
      }
    }

    @include lg-block {
      font-size: 16px;
      line-height: 24px;
    }
  }
}

.no-mobile {
  display: none;
  @include md-block {
    display: initial;
  }
}

.bold {
  font-weight: 600;
}
</style>
